import { useEffect, useState } from 'react';
import Panel from './Panel.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import {
  getAccountCredits, createCredit, voidCredit, refundCredit,
} from '@/services/deals.service.js';
import { getAccountTerms } from '@/services/account.service.js';

// Account money-zone credits surface (REWRITE-AND-CREDITS-SPEC §B3.2 + §B5).
// Lists credits sourced from this account, the manual "Add credit" true-up
// door, void, and the D8 `fronted` flag (carrier remit pending).

const STATUS_VARIANT = {
  available: 'info',
  applied: 'warning',
  settled: 'success',
  refunded: 'muted',
  voided: 'muted',
};

function money(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function CreditsPanel({ accountId, refreshKey }) {
  const [credits, setCredits] = useState(null);
  const [terms, setTerms] = useState([]);
  const [adding, setAdding] = useState(false);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [sourceDealId, setSourceDealId] = useState('');
  const [busy, setBusy] = useState(false);
  const [bump, setBump] = useState(0);
  const [voidingId, setVoidingId] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  // Refund: an AMOUNT and a DATE, never a status word. Blank amount means the
  // whole credit; a smaller one splits the row and leaves the remainder
  // available, because a credit can be $164 with $50 refunded.
  const [refundingId, setRefundingId] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundOn, setRefundOn] = useState('');

  useEffect(() => {
    if (!accountId) return;
    let alive = true;
    getAccountCredits(accountId)
      .then((r) => { if (alive) setCredits(r?.credits || []); })
      .catch(() => { if (alive) setCredits([]); });
    return () => { alive = false; };
  }, [accountId, refreshKey, bump]);

  useEffect(() => {
    if (!accountId || !adding) return;
    getAccountTerms(accountId)
      .then((r) => setTerms([...(r?.current || []), ...(r?.historical || [])]))
      .catch(() => setTerms([]));
  }, [accountId, adding]);

  async function handleAdd() {
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error('Amount must be > 0'); return; }
    if (!sourceDealId) { toast.error('Pick a source deal (no orphan credits)'); return; }
    setBusy(true);
    try {
      const res = await createCredit({
        source_crm_deal_id: sourceDealId,
        source_account_id: accountId,
        amount: amt,
        memo: memo.trim() || null,
      });
      if (!res?.success) { toast.error(res?.error || 'Create failed'); return; }
      toast.success(`Credit created (${money(amt)})`);
      setAdding(false); setAmount(''); setMemo(''); setSourceDealId('');
      setBump((b) => b + 1);
    } catch (e) {
      toast.error(e?.body?.error || e?.message || 'Create failed');
    } finally { setBusy(false); }
  }

  async function handleRefund(creditId) {
    if (!refundReason.trim()) { toast.error('A refund reason is required'); return; }
    setBusy(true);
    try {
      const res = await refundCredit(creditId, { amount: refundAmount, reason: refundReason.trim(), refunded_on: refundOn });
      if (!res?.success) { toast.error(res?.error || 'Refund failed'); return; }
      toast.success(res.partial
        ? `Refunded ${money(res.refunded)}; ${money(res.remaining)} still available`
        : `Refunded ${money(res.refunded)}`);
      setRefundingId(null); setRefundAmount(''); setRefundReason(''); setRefundOn('');
      setBump((b) => b + 1);
    } catch (e) {
      toast.error(e?.body?.error || e?.message || 'Refund failed');
    } finally { setBusy(false); }
  }

  async function handleVoid(creditId) {
    if (!voidReason.trim()) { toast.error('A void reason is required'); return; }
    try {
      const res = await voidCredit(creditId, voidReason.trim());
      if (!res?.success) { toast.error(res?.error || 'Void failed'); return; }
      toast.success('Credit voided');
      setVoidingId(null); setVoidReason('');
      setBump((b) => b + 1);
    } catch (e) {
      toast.error(e?.body?.error || e?.message || 'Void failed');
    }
  }

  if (!accountId) return null;
  const list = credits || [];
  const available = list.filter((c) => c.status === 'available');

  return (
    <Panel
      title="Credits"
      count={credits === null ? undefined : list.length}
      empty={credits === null ? 'Loading…' : (list.length === 0 && !adding ? 'No account credits.' : null)}
      footer={
        adding ? (
          <div className="space-y-2 py-1">
            <div className="flex gap-2">
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="w-32" />
              <select
                className="flex-1 rounded-card border border-border bg-bg px-2 py-1 text-sm text-text"
                value={sourceDealId}
                onChange={(e) => setSourceDealId(e.target.value)}
              >
                <option value="">Source deal…</option>
                {terms.map((t) => (
                  <option key={t.deal_id} value={t.deal_id}>
                    {t.policy_number || t.deal_name || t.deal_id}
                  </option>
                ))}
              </select>
            </div>
            <Input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Memo (optional)" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)} disabled={busy}>Cancel</Button>
              <Button size="sm" onClick={handleAdd} disabled={busy}>{busy ? 'Adding…' : 'Add credit'}</Button>
            </div>
          </div>
        ) : (
          <button type="button" className="text-accent hover:underline" onClick={() => setAdding(true)}>
            + Add credit
          </button>
        )
      }
    >
      {list.length > 0 ? (
        <div className="space-y-1.5">
          {list.map((c) => (
            <div key={c.id} className="rounded-card border border-border bg-bg/40 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-text">{money(c.amount)}</span>
                    {/* The FALLBACK is `outline`, not `muted`. All five values the
                        deal_credits CHECK allows are mapped above, so this cannot
                        fire today, but `muted` line-throughs, and defaulting an
                        UNRECOGNISED status to "cancelled" is the trap that struck
                        through Full Flight and the quote status. A status nobody
                        mapped is unknown, not dead. */}
                    <Badge variant={STATUS_VARIANT[c.status] || 'outline'}>{c.status}</Badge>
                    {c.fronted && <Badge variant="warning">carrier remit pending</Badge>}
                  </div>
                  <div className="truncate text-[11px] text-text-dim">
                    {c.memo || `from deal ${c.source_crm_deal_id}`}
                    {c.target_crm_deal_id ? ` → deal ${c.target_crm_deal_id}` : ''}
                  </div>
                </div>
                <span className="flex shrink-0 items-center gap-2">
                  {/* Only an AVAILABLE credit can be refunded: an applied one is
                      spoken for and a settled one has already moved. */}
                  {c.status === 'available' && refundingId !== c.id && voidingId !== c.id && (
                    <button type="button" className="text-[11px] text-text-dim hover:text-success hover:underline" onClick={() => { setRefundingId(c.id); setRefundAmount(''); setRefundReason(''); setRefundOn(''); }}>
                      refund
                    </button>
                  )}
                  {['available', 'applied'].includes(c.status) && voidingId !== c.id && refundingId !== c.id && (
                    <button type="button" className="text-[11px] text-text-dim hover:text-danger hover:underline" onClick={() => { setVoidingId(c.id); setVoidReason(''); }}>
                      void
                    </button>
                  )}
                </span>
              </div>
              {refundingId === c.id && (
                <div className="mt-2 space-y-2">
                  <div className="flex gap-2">
                    <Input type="number" step="0.01" min="0" max={c.amount} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} placeholder={`Amount (blank = all ${money(c.amount)})`} className="w-44" />
                    <Input type="date" value={refundOn} onChange={(e) => setRefundOn(e.target.value)} className="w-40" title="The day the money went back. Blank = today." />
                  </div>
                  <Input value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Refund reason (required)" />
                  <p className="text-[10px] text-text-dim">Records a refund you have already made. Nothing is sent or moved from here.</p>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => { setRefundingId(null); setRefundAmount(''); setRefundReason(''); setRefundOn(''); }} disabled={busy}>Cancel</Button>
                    <Button size="sm" onClick={() => handleRefund(c.id)} disabled={busy}>{busy ? 'Recording…' : 'Record refund'}</Button>
                  </div>
                </div>
              )}
              {voidingId === c.id && (
                <div className="mt-2 flex gap-2">
                  <Input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="Void reason (required)" className="flex-1" />
                  <Button size="sm" variant="ghost" onClick={() => { setVoidingId(null); setVoidReason(''); }}>Cancel</Button>
                  <Button size="sm" onClick={() => handleVoid(c.id)}>Void</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}
      {available.length > 0 && (
        <p className="mt-2 text-[10px] text-text-dim">
          {money(available.reduce((s, c) => s + Number(c.amount || 0), 0))} available to apply on a payment request.
        </p>
      )}
    </Panel>
  );
}
